/**
 * Floating Add to Cart bar (product pages). Rebuilt 18 Sep 2026 for Ape the Label.
 * Markup: snippets/floating-atc.liquid   Styles: assets/floating-atc.css
 *
 * The bar never adds to cart by itself. It presses the product page's real Add to Cart button,
 * so the chosen size, quantity, stock rules, error messages and the cart drawer behave exactly
 * as they do from the main button. For a sold-out size it becomes "Notify me" and presses Klaviyo's
 * back-in-stock button instead (Klaviyo hides Add to Cart and shows its own button for sold-out sizes).
 * It stays in sync through the theme's own signals:
 *   - variant-change (theme pub/sub): size, price, compare-at price, stock, variant image
 *   - theme:cart:added: success
 *   - the main form's error container: errors are repeated in the bar, because the form is off screen
 * Visibility uses IntersectionObserver (no scroll listeners): the bar shows once the main button has
 * scrolled up under the header, and hides when that button is back in view or the footer arrives.
 */
(() => {
  if (customElements.get('floating-atc')) return;

  const OPEN_CLASS = 'floating-atc-open';

  class FloatingAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-floating-button]');
      this.label = this.querySelector('[data-floating-label]');
      this.price = this.querySelector('[data-floating-price]');
      this.compare = this.querySelector('[data-floating-compare]');
      this.picker = this.querySelector('[data-floating-variant]');
      this.image = this.querySelector('.floating-atc__image');
      this.message = this.querySelector('[data-floating-message]');
      if (!this.button || !this.label) return;

      this.strings = {
        add: this.dataset.labelAdd || 'Add to Cart',
        soldOut: this.dataset.labelSoldOut || 'Sold Out',
        unavailable: this.dataset.labelUnavailable || 'Unavailable',
        added: this.dataset.labelAdded || 'Added',
        notify: this.dataset.labelNotify || 'Notify me',
      };
      this.currentLabel = this.label.textContent.trim();
      this.state = { past: false, footer: false };
      this.observers = [];
      this.cleanups = [];
      this.pending = false;
      this.shown = false;
      this.mode = 'add';
      this.variantAvailable = this.dataset.available !== 'false';

      if (!this.bind()) return;

      this.button.addEventListener('click', () => this.addToCart());
      if (this.picker) {
        this.picker.addEventListener('change', () => this.choose(this.picker.selectedOptions[0]));
      }

      this.on(document, 'theme:cart:added', () => this.finish(true));
      this.on(window, 'resize', () => this.measure(), { passive: true });
      this.on(document, 'shopify:section:load', () => {
        this.disconnectObservers();
        if (this.bind()) this.watch();
      });

      if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
        const unsubscribe = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || !this.product || data.sectionId !== this.product.dataset.section) return;
          this.sync(data.variant);
        });
        this.cleanups.push(unsubscribe);
      }

      this.watch();
    }

    disconnectedCallback() {
      this.disconnectObservers();
      (this.cleanups || []).forEach((cleanup) => typeof cleanup === 'function' && cleanup());
      document.documentElement.classList.remove(OPEN_CLASS);
    }

    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    disconnectObservers() {
      (this.observers || []).forEach((observer) => observer.disconnect());
      this.observers = [];
    }

    /* Find the product page's own form and Add to Cart button. */
    bind() {
      const id = this.dataset.productId;
      this.product =
        document.querySelector(`product-info[id^="MainProduct--"][data-product-id="${id}"]`) ||
        document.querySelector(`product-info[data-product-id="${id}"]`);
      this.form = this.product ? this.product.querySelector('form[id^="AddToCartForm--"]') : null;
      this.mainButton = this.form ? this.form.querySelector('[data-add-to-cart], button[type="submit"]') : null;
      this.errors = this.form ? this.form.querySelector('[data-cart-errors-container]') : null;
      // The buttons area holds Add to Cart and, for sold-out sizes, Klaviyo's "Notify me" button.
      this.holder = this.mainButton
        ? this.mainButton.closest('.product__submit__holder') || this.mainButton.parentElement
        : null;
      return Boolean(this.mainButton);
    }

    watch() {
      const header =
        window.stickyHeaderHeight ||
        parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-height'), 10) ||
        0;

      // Show once the page's buttons have scrolled up under the sticky header. The watched area runs from
      // the header line down past the end of the page, so the button counts as "passed" only when it is
      // above that line, and a jump straight past it (fast flick, reload, back button) is still caught.
      const buttonObserver = new IntersectionObserver(
        ([entry]) => {
          this.state.past = !entry.isIntersecting && entry.boundingClientRect.bottom <= header + 1;
          this.render();
        },
        { rootMargin: `-${header}px 0px 100000px 0px` }
      );
      buttonObserver.observe(this.holder);
      this.observers.push(buttonObserver);

      // Step aside for the footer.
      const footer = document.querySelector('footer.site-footer, [data-section-type="footer"]');
      if (footer) {
        const footerObserver = new IntersectionObserver(
          ([entry]) => {
            this.state.footer = entry.isIntersecting;
            this.render();
          },
          { rootMargin: '0px 0px -40px 0px' }
        );
        footerObserver.observe(footer);
        this.observers.push(footerObserver);
      }

      // Mirror the main button's loading state while an add started here is in flight.
      const loadingObserver = new MutationObserver(() => {
        if (this.pending) this.classList.toggle('is-loading', this.mainButton.classList.contains('is-loading'));
      });
      loadingObserver.observe(this.mainButton, { attributes: true, attributeFilter: ['class'] });
      this.observers.push(loadingObserver);

      // The theme writes add-to-cart errors next to the main form; repeat them in the bar.
      if (this.errors) {
        const errorObserver = new MutationObserver(() => {
          const error = this.errors.querySelector('[data-error]');
          if (!this.pending || !error) return;
          this.finish(false);
          this.say(error.textContent.replace(/\s+/g, ' ').trim());
        });
        errorObserver.observe(this.errors, { childList: true, subtree: true });
        this.observers.push(errorObserver);
      }

      // Klaviyo adds / removes its "Notify me" button (and hides / shows Add to Cart) after size changes.
      const modeObserver = new MutationObserver(() => this.scheduleMode());
      modeObserver.observe(this.holder, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden'],
      });
      this.observers.push(modeObserver);
      this.updateMode();
    }

    render() {
      const buttonsShown = this.holder.getClientRects().length > 0; // hidden for B2B accounts
      const show = this.state.past && !this.state.footer && buttonsShown;
      if (show === this.shown) return;
      this.shown = show;
      if (show) this.measure();
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.documentElement.classList.toggle(OPEN_CLASS, show);
      if (!show) this.hideMessage();
    }

    /* How far the rewards launcher lifts: bar height + its bottom offset + a small gap. */
    measure() {
      const bottom = parseFloat(getComputedStyle(this).bottom) || 0;
      const lift = Math.round(this.offsetHeight + bottom + 10);
      document.documentElement.style.setProperty('--floating-atc-lift', `${lift}px`);
    }

    addToCart() {
      if (this.button.disabled) return;
      if (this.mode === 'notify') {
        const target = this.notifyTarget();
        if (target) target.click(); // opens Klaviyo's back-in-stock sign-up
        return;
      }
      if (this.pending || this.mode !== 'add') return;
      if (!this.mainButton.isConnected && !this.bind()) return;
      this.hideMessage();
      this.pending = true;
      this.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(() => this.finish(false), 12000);
      this.mainButton.click();
    }

    finish(success) {
      if (!this.pending) return;
      this.pending = false;
      clearTimeout(this.pendingTimer);
      this.classList.remove('is-loading');
      this.button.removeAttribute('aria-busy');
      if (!success) return;
      this.classList.add('is-added');
      this.label.textContent = this.strings.added;
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => {
        this.classList.remove('is-added');
        this.label.textContent = this.currentLabel;
      }, 1800);
    }

    /* A size picked in the bar is selected on the page, so the theme updates price, stock and URL. */
    choose(option) {
      if (!option || !this.product) return;
      let values = [];
      try {
        values = JSON.parse(option.dataset.options || '[]');
      } catch (error) {
        values = [];
      }
      const selects = this.product.querySelector('variant-selects');
      if (!selects || !values.length) return;

      let changed = null;
      values.forEach((value, index) => {
        const name = `option${index + 1}`;
        const radio = Array.from(selects.querySelectorAll(`input[type="radio"][data-index="${name}"]`)).find(
          (input) => input.value === value
        );
        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            changed = radio;
          }
          return;
        }
        const dropdown = selects.querySelector(`select[data-index="${name}"]`);
        if (dropdown && dropdown.value !== value) {
          dropdown.value = value;
          changed = dropdown;
        }
      });
      if (changed) {
        this.holdScroll(document.activeElement === this.picker);
        changed.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* After a size change the theme focuses the page's own size button, which scrolls the page up
       to it (and would tuck this bar away). Keep the shopper where they are and hand focus back. */
    holdScroll(refocus) {
      const y = window.scrollY;
      const jump = () => {
        if (Math.abs(window.scrollY - y) <= 1) return;
        const root = document.documentElement;
        const previous = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto'; // the theme scrolls smoothly; this must be instant
        window.scrollTo(0, y);
        root.style.scrollBehavior = previous;
      };
      const release = () => {
        this.product.removeEventListener('focusin', onFocus);
        clearTimeout(this.holdTimer);
        this.restoreScroll = null;
      };
      const onFocus = (event) => {
        if (!event.target.closest || !event.target.closest('variant-selects')) return;
        // focus() scrolls only after its focus events have fired, so restore once it has finished
        queueMicrotask(() => {
          jump();
          release();
          if (refocus && this.picker) this.picker.focus({ preventScroll: true });
        });
      };
      if (this.restoreScroll) this.restoreScroll.release();
      this.restoreScroll = jump;
      this.restoreScroll.release = release;
      this.product.addEventListener('focusin', onFocus);
      clearTimeout(this.holdTimer);
      this.holdTimer = setTimeout(release, 5000);
    }

    /* Called with the theme's variant data after every size change on the page. */
    sync(variant) {
      if (this.restoreScroll) this.restoreScroll();
      if (!variant) {
        this.variantAvailable = null;
        this.updateMode();
        return;
      }
      if (this.picker && this.picker.value !== String(variant.id)) this.picker.value = String(variant.id);

      const money = (cents) =>
        window.theme && typeof window.theme.formatMoney === 'function'
          ? window.theme.formatMoney(cents, window.theme.moneyFormat)
          : '';
      const price = money(variant.price);
      if (price && this.price) this.price.textContent = price;
      if (this.compare) {
        const onSale = variant.compare_at_price > variant.price;
        this.compare.hidden = !onSale;
        this.compare.textContent = onSale ? money(variant.compare_at_price) : '';
      }

      const src = variant.featured_image && variant.featured_image.src;
      if (src && this.image) {
        this.image.removeAttribute('srcset');
        this.image.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
      }

      this.variantAvailable = Boolean(variant.available);
      this.updateMode();
    }

    scheduleMode() {
      if (this.modeFrame) return;
      this.modeFrame = requestAnimationFrame(() => {
        this.modeFrame = null;
        this.updateMode();
      });
    }

    /* What the bar's button does right now: Add to Cart, Notify me, or nothing (sold out / unavailable). */
    updateMode() {
      if (this.notifyTarget()) this.setMode('notify', true, this.strings.notify);
      else if (this.variantAvailable === null) this.setMode('unavailable', false, this.strings.unavailable);
      else if (this.variantAvailable) this.setMode('add', true, this.strings.add);
      else this.setMode('soldout', false, this.strings.soldOut);
    }

    /* The page's "Notify me when available" button (assets/notify-me.js turns sold-out Add to Cart buttons
       into it), else Klaviyo's own back-in-stock button, else the theme's notify pop-up if ever switched on. */
    notifyTarget() {
      if (this.mainButton.hasAttribute('data-notify-me')) return this.mainButton;
      const box = document.getElementById('klaviyo-bis-button-container');
      const klaviyo = box && this.product && this.product.contains(box) ? box.querySelector('button') : null;
      if (klaviyo && klaviyo.getClientRects().length > 0) return klaviyo;
      const wrapper = this.form ? this.form.closest('[data-form-wrapper]') : null;
      if (this.mainButton.hasAttribute('data-notification-popup') && wrapper && wrapper.classList.contains('variant--soldout')) {
        return this.mainButton;
      }
      return null;
    }

    setMode(mode, enabled, label) {
      this.mode = mode;
      this.classList.toggle('is-notify', mode === 'notify');
      this.button.disabled = !enabled;
      this.currentLabel = label;
      if (!this.classList.contains('is-added')) this.label.textContent = label;
    }

    say(text) {
      if (!text || !this.message) return;
      this.message.textContent = text;
      this.classList.add('has-message');
      clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => this.hideMessage(), 6000);
    }

    hideMessage() {
      this.classList.remove('has-message');
    }
  }

  customElements.define('floating-atc', FloatingAtc);
})();
